import { useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  downloadRoundPack,
  downloadSavez,
  emailRoundPack,
  getPlayerOfRound,
  getRound,
  listRounds,
  me,
  publishPlayerOfRound,
  sharePlayerOfRound,
  type AwardCandidate,
  type MatchReportEvent,
} from '../api/portal';
import { runClipRule } from '../api/clips';
import { ApiError } from '../api/client';
import { isLeagueLogin } from '../lib/portalRole';

export function RoundsPage() {
  const { key } = useParams();
  const profile = useQuery({ queryKey: ['portal-me'], queryFn: me });
  const list = useQuery({ queryKey: ['portal-rounds'], queryFn: listRounds });
  const detail = useQuery({
    queryKey: ['portal-round', key],
    queryFn: () => getRound(key!),
    enabled: !!key,
  });
  const qc = useQueryClient();
  const [packMsg, setPackMsg] = useState<string | null>(null);
  const award = useQuery({
    queryKey: ['portal-player-of-round', key],
    queryFn: () => getPlayerOfRound(key!),
    enabled: !!key,
  });
  const zip = useMutation({
    mutationFn: () => downloadRoundPack(key!),
    onSuccess: () => setPackMsg('ZIP preuzet.'),
    onError: (e) => setPackMsg(e instanceof ApiError ? e.message : String(e)),
  });
  const mail = useMutation({
    mutationFn: () => emailRoundPack(key!),
    onSuccess: (res) => setPackMsg(`Poslato na ${res.to}`),
    onError: (e) => setPackMsg(e instanceof ApiError ? e.message : String(e)),
  });
  const savez = useMutation({
    mutationFn: (format: 'zip' | 'xml' | 'csv') => downloadSavez(key!, format),
    onSuccess: () => setPackMsg('Savez / EHF export preuzet.'),
    onError: (e) => setPackMsg(e instanceof ApiError ? e.message : String(e)),
  });
  const publishAward = useMutation({
    mutationFn: (c?: AwardCandidate) =>
      publishPlayerOfRound(key!, c ? { person_id: c.person_id, match_id: c.match_id } : {}),
    onSuccess: (res) => {
      qc.invalidateQueries({ queryKey: ['portal-player-of-round', key] });
      setPackMsg(`Objavljeno: ${res.player}`);
    },
    onError: (e) => setPackMsg(e instanceof ApiError ? e.message : String(e)),
  });
  const shareAward = useMutation({
    mutationFn: (mode: 'now' | 'morning') => sharePlayerOfRound(key!, { mode }),
    onSuccess: (res) => setPackMsg(res.mode === 'morning'
      ? `Copy zakazan za ${res.release_at?.replace('T', ' ').slice(0, 16) ?? 'jutro'}`
      : 'Copy poslat na Telegram.'),
    onError: (e) => setPackMsg(e instanceof ApiError ? e.message : String(e)),
  });
  const reel = useMutation({
    mutationFn: async (c: AwardCandidate) => {
      const run = await runClipRule('player_reel', {
        match_id: c.match_id,
        person_id: c.person_id,
        aspect: '9:16',
        publish_mode: 'auto',
      });
      const reelId = run.vertical_reel_id || run.reel_id;
      if (run.status !== 'ok' || !reelId) {
        throw new Error(run.reason || 'Nema klipova za 9:16 reel.');
      }
      await publishPlayerOfRound(key!, {
        person_id: c.person_id,
        match_id: c.match_id,
        reel_id: reelId,
      });
      return run;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['portal-player-of-round', key] });
      qc.invalidateQueries({ queryKey: ['highlights'] });
      setPackMsg('9:16 reel u redu. Copy je spreman za morning pack.');
    },
    onError: (e) => setPackMsg(e instanceof ApiError ? e.message : String(e)),
  });

  return (
    <div>
      <div className="page-head">
        <div className="page-kicker">Kolo</div>
        <h1 className="page-title">{detail.data?.label || 'Kola'}</h1>
        <p className="page-sub">Strelci, 7m, isključenja — isti podaci kao zapisnik, po kolu. Liga skida jedan ZIP za sekretara.</p>
        {key && isLeagueLogin(profile.data) && (
          <div className="flex flex-wrap gap-2">
            <button type="button" className="btn-primary text-xs" disabled={zip.isPending} onClick={() => zip.mutate()}>
              {zip.isPending ? 'ZIP…' : 'Preuzmi kolo pack'}
            </button>
            <button type="button" className="btn-secondary text-xs" disabled={mail.isPending} onClick={() => mail.mutate()}>
              {mail.isPending ? 'Slanje…' : 'Pošalji mejlom'}
            </button>
            <button type="button" className="btn-ghost text-xs" disabled={savez.isPending} onClick={() => savez.mutate('zip')}>
              Savez ZIP
            </button>
            <button type="button" className="btn-ghost text-xs" disabled={savez.isPending} onClick={() => savez.mutate('xml')}>
              XML
            </button>
            <button type="button" className="btn-ghost text-xs" disabled={savez.isPending} onClick={() => savez.mutate('csv')}>
              CSV
            </button>
          </div>
        )}
      </div>
      {packMsg && <div className="card mb-4 p-3 text-sm text-slate-200">{packMsg}</div>}
      <div className="mb-4 flex flex-wrap gap-2">
        {(list.data ?? []).map((r) => (
          <Link
            key={r.key}
            to={`/rounds/${encodeURIComponent(r.key)}`}
            className={key === r.key
              ? 'rounded-full bg-accent px-3 py-1.5 text-xs font-semibold text-white'
              : 'rounded-full px-3 py-1.5 text-xs font-semibold text-subtle ring-1 ring-white/10 hover:text-white'}
          >
            {r.label} <span className="opacity-70">{r.matches}</span>
          </Link>
        ))}
        {(list.data ?? []).length === 0 && (
          <div className="text-sm text-slate-500">No rounds in the entitled catalog yet.</div>
        )}
      </div>
      {detail.data && (
        <>
          <div className="card mb-4 overflow-hidden">
            <table className="table">
              <thead>
                <tr><th>Match</th><th>Score</th><th></th></tr>
              </thead>
              <tbody>
                {detail.data.matches.map((m) => (
                  <tr key={m.id}>
                    <td className="text-white">{m.home} vs {m.away}</td>
                    <td className="font-mono">{m.score_home}–{m.score_away}</td>
                    <td className="flex gap-2">
                      {m.official && <span className="text-[11px] text-slate-300">Zvanično</span>}
                      <Link to={`/matches/${m.id}/report`} className="text-xs text-accent hover:underline">Zapisnik</Link>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <div className="grid gap-4 md:grid-cols-3">
            <List title="Strelci" rows={detail.data.scorers} />
            <List title="7m" rows={detail.data.seven_meters} />
            <List title="Isključenja" rows={detail.data.exclusions} />
          </div>
          <AwardBlock
            award={award.data}
            league={isLeagueLogin(profile.data)}
            busy={publishAward.isPending || shareAward.isPending || reel.isPending}
            onPublish={(c) => publishAward.mutate(c)}
            onShare={(mode) => shareAward.mutate(mode)}
            onReel={(c) => reel.mutate(c)}
          />
        </>
      )}
    </div>
  );
}

function AwardBlock({
  award,
  league,
  busy,
  onPublish,
  onShare,
  onReel,
}: {
  award?: { candidates: AwardCandidate[]; published?: { player: string; team?: string; copy: string; reel_id?: string } | null };
  league: boolean;
  busy: boolean;
  onPublish: (c?: AwardCandidate) => void;
  onShare: (mode: 'now' | 'morning') => void;
  onReel: (c: AwardCandidate) => void;
}) {
  const published = award?.published;
  const top = award?.candidates?.[0];
  return (
    <div className="card mt-4 p-4">
      <div className="mb-2 flex flex-wrap items-center justify-between gap-2">
        <div>
          <div className="text-[11px] uppercase tracking-wide text-slate-500">Igrač kola</div>
          <p className="text-sm text-slate-400">Auto predlog (golovi, 7m, ocene klipova) + 9:16 reel + copy za morning pack.</p>
        </div>
        {league && (
          <div className="flex flex-wrap gap-1">
            <button type="button" className="btn-primary text-xs" disabled={busy || !top} onClick={() => onPublish()}>
              Objavi predlog
            </button>
            {published && (
              <>
                <button type="button" className="btn-ghost text-xs" disabled={busy} onClick={() => onShare('now')}>Pošalji copy</button>
                <button type="button" className="btn-ghost text-xs" disabled={busy} onClick={() => onShare('morning')}>Morning pack</button>
              </>
            )}
          </div>
        )}
      </div>
      {published && (
        <div className="mb-3 rounded-lg bg-white/5 p-3 text-sm text-slate-200">
          <span className="font-semibold text-white">{published.player}</span>
          {published.team ? ` · ${published.team}` : ''}
          <div className="mt-1 text-slate-400">{published.copy}</div>
          {published.reel_id && <div className="mt-1 text-[11px] text-slate-500">Reel {published.reel_id}</div>}
        </div>
      )}
      <table className="table">
        <thead>
          <tr><th>Igrač</th><th>Golovi</th><th>7m</th><th>Ocena</th><th>Klipovi</th><th></th></tr>
        </thead>
        <tbody>
          {(award?.candidates ?? []).map((c) => (
            <tr key={c.person_id}>
              <td className="text-white">{c.player} <span className="text-slate-500">{c.team}</span></td>
              <td className="tabular-nums">{c.goals}</td>
              <td className="tabular-nums">{c.seven_meter_goals}</td>
              <td className="tabular-nums">{c.avg_rating.toFixed(1)}</td>
              <td className="tabular-nums">{c.clips}</td>
              <td className="flex flex-wrap gap-1">
                {league && (
                  <>
                    <button type="button" className="btn-ghost text-xs" disabled={busy} onClick={() => onPublish(c)}>Objavi</button>
                    <button type="button" className="btn-ghost text-xs" disabled={busy} onClick={() => onReel(c)}>9:16 reel</button>
                  </>
                )}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
      {(award?.candidates ?? []).length === 0 && (
        <div className="py-4 text-center text-sm text-slate-500">Nema kandidata — treba official golovi / klipovi.</div>
      )}
    </div>
  );
}

function List({ title, rows }: { title: string; rows: MatchReportEvent[] }) {
  return (
    <div className="card p-4">
      <div className="mb-2 text-[11px] uppercase tracking-wide text-slate-500">{title}</div>
      {rows.slice(0, 20).map((e) => (
        <div key={e.id} className="flex justify-between gap-2 py-1 text-sm">
          <span className="text-white">{e.player || e.event_type}</span>
          <span className="text-xs text-slate-500">{e.team}{e.minute != null ? ` · ${e.minute}'` : ''}</span>
        </div>
      ))}
      {rows.length === 0 && <div className="text-sm text-slate-500">—</div>}
    </div>
  );
}
